// 数学服务主类 - 统一的数学渲染和输入接口

import { MathRenderer, MathInput, MathRenderOptions, MathInputOptions, MathInputInstance } from './types';
import { getMathConfig } from './config';
import { CustomMathRenderer } from './renderers/CustomRenderer';
import { KaTeXRenderer } from './renderers/KaTeXRenderer';
// import { MathLiveInput } from './input/MathLiveInput';

export class MathService {
  private renderer: MathRenderer;
  private input: MathInput | null = null;
  private config = getMathConfig();

  constructor() {
    this.renderer = this.createRenderer();
    if (this.config.inputEnabled) {
      this.input = this.createInputService();
    }
  }

  private createRenderer(): MathRenderer {
    switch (this.config.renderer) {
      case 'katex':
        if (this.config.fallbackEnabled) {
          return new FallbackRenderer(new KaTeXRenderer(), new CustomMathRenderer());
        }
        return new KaTeXRenderer();
      case 'custom':
      default:
        return new CustomMathRenderer();
    }
  }

  private createInputService(): MathInput | null {
    if (!this.config.inputEnabled) return null;
    
    try {
      // 暂时禁用 MathLiveInput 以避免模块导入问题
      // return new MathLiveInput();
      console.warn('MathLive input temporarily disabled due to module import issues');
      return null;
    } catch (error) {
      console.warn('Failed to create math input:', error);
      return null;
    }
  }

  // 渲染方法
  async render(latex: string, options?: MathRenderOptions): Promise<string> {
    try {
      if (this.renderer instanceof KaTeXRenderer) {
        return await (this.renderer as KaTeXRenderer).render(latex, options);
      }
      return this.renderer.render(latex, options);
    } catch (error) {
      console.warn('Math render error:', error);
      throw error;
    }
  }

  async renderToElement(latex: string, element: HTMLElement, options?: MathRenderOptions): Promise<void> {
    try {
      if (this.renderer instanceof KaTeXRenderer) {
        await (this.renderer as KaTeXRenderer).renderToElement(latex, element, options);
      } else {
        this.renderer.renderToElement(latex, element, options);
      }
    } catch (error) {
      console.warn('Math render to element error:', error);
      throw error;
    }
  }

  // 输入方法
  async createInput(container: HTMLElement, options?: MathInputOptions): Promise<MathInputInstance | null> {
    if (!this.input) return null;

    try {
      const inputOptions = {
        virtualKeyboard: this.config.virtualKeyboard,
        ...options
      };
      
      return await this.input.create(container, inputOptions);
    } catch (error) {
      console.warn('Math input create error:', error);
      return null;
    }
  }

  // 工具方法
  isInputSupported(): boolean {
    return this.input?.isSupported() || false;
  }

  isRenderSupported(): boolean {
    return this.renderer.isSupported();
  }

  getConfig() {
    return { ...this.config };
  }
}

// 降级渲染器 - 主渲染器失败时使用备用渲染器
class FallbackRenderer implements MathRenderer {
  constructor(
    private primary: MathRenderer,
    private fallback: MathRenderer
  ) {}

  isSupported(): boolean {
    return this.primary.isSupported() || this.fallback.isSupported();
  }

  async render(latex: string, options?: MathRenderOptions): Promise<string> {
    try {
      if (this.primary instanceof KaTeXRenderer) {
        return await (this.primary as KaTeXRenderer).render(latex, options);
      }
      return this.primary.render(latex, options);
    } catch (error) {
      console.warn('Primary renderer failed, using fallback:', error);
      return this.fallback.render(latex, options);
    }
  }

  async renderToElement(latex: string, element: HTMLElement, options?: MathRenderOptions): Promise<void> {
    try {
      if (this.primary instanceof KaTeXRenderer) {
        await (this.primary as KaTeXRenderer).renderToElement(latex, element, options);
      } else {
        this.primary.renderToElement(latex, element, options);
      }
    } catch (error) {
      console.warn('Primary renderer failed, using fallback:', error);
      this.fallback.renderToElement(latex, element, options);
    }
  }
}

// 单例实例
export const mathService = new MathService();